import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Typography,
  IconButton,
  useTheme,
  Tooltip,
  Box,
  Chip,
} from "@mui/material";
import Delete from "@mui/icons-material/Delete";
import Visibility from "@mui/icons-material/Visibility";
import {
  DocumentStatus,
  Issue,
  SubmissionDocumentType,
} from "generated-graphql/graphql";
import { useDocumentDownloadLink } from "hooks/useDocumentDownloadLink";
import { useAlerts } from "components/Alerts/AlertProvider";
import { brandColors } from "../../util";
import { IssueCount } from "components/IssueCount";

export interface SubmissionDocument {
  id: string;
  name: string;
  documentType: string;
  submissionDocumentType: SubmissionDocumentType;
  status?: DocumentStatus;
  file?: File;
  url?: string;
}

// Helper component for truncating text with tooltip
const TruncatedText = ({
  text,
  maxLength = 30,
}: {
  text: string;
  maxLength?: number;
}) => {
  if (!text || text.length <= maxLength) {
    return <span>{text}</span>;
  }
  const truncated = text.substring(0, maxLength) + "...";
  return (
    <Tooltip title={text} arrow placement="top">
      <Box
        component="span"
        sx={{
          cursor: "help",
          textOverflow: "ellipsis",
          overflow: "hidden",
          whiteSpace: "nowrap",
          display: "block",
        }}
      >
        {truncated}
      </Box>
    </Tooltip>
  );
};

function DocumentStatusCell({ doc }: { doc: SubmissionDocument }) {
  const theme = useTheme();

  if (
    doc.submissionDocumentType === SubmissionDocumentType.SignedSubmission
  ) {
    return (
      <TableCell>
        <Chip
          label="Signed"
          sx={{
            color: theme.palette.success.main,
            backgroundColor: brandColors["encamp-green"][200],
          }}
          size="small"
        />
      </TableCell>
    );
  }

  if (
    doc.submissionDocumentType === SubmissionDocumentType.RejectedSubmission
  ) {
    return (
      <TableCell>
        <Chip
          label="Rejected"
          sx={{
            color: theme.palette.error.main,
            backgroundColor: brandColors["encamp-red"][200],
          }}
          size="small"
        />
      </TableCell>
    );
  }

  return (
    <TableCell>
      {doc.status ? (
        <Typography variant="body2" color={getDocumentStatusColor(doc.status)}>
          {doc.status}
        </Typography>
      ) : (
        <Typography variant="body2" color="text.disabled">
          —
        </Typography>
      )}
    </TableCell>
  );
}

// Helper function to determine document status color
function getDocumentStatusColor(status: DocumentStatus): string {
  switch (status) {
    case DocumentStatus.Declined:
    case DocumentStatus.Revoked:
    case DocumentStatus.Expired:
      return "error";
    case DocumentStatus.InProgress:
      return "warning.main";
    case DocumentStatus.Draft:
      return "info.main";
    default:
      return "success.main";
  }
}

interface DocumentUploadSectionProps {
  documents: SubmissionDocument[];
  onDeleteDocument: (documentId: string) => void;
  onSelectDocument?: (document: SubmissionDocument) => void;
  issues?: Issue[];
}

export function DocumentUploadSection({
  documents,
  onDeleteDocument,
  onSelectDocument,
  issues = [],
}: DocumentUploadSectionProps) {
  const theme = useTheme();
  const alerts = useAlerts();
  const [getDocumentDownloadLink] = useDocumentDownloadLink();

  const handleDocumentDownload = async (doc: SubmissionDocument) => {
    if (!doc.id) {
      alerts.error("Unable to download document: No document ID");
      return;
    }

    try {
      const { data } = await getDocumentDownloadLink({
        variables: { id: doc.id },
      });

      if (data?.getDocumentDownloadLink) {
        // Create a temporary anchor element to force download
        const link = document.createElement("a");
        link.href = data.getDocumentDownloadLink;
        link.download = doc.name || "document";
        link.style.display = "none";
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      } else {
        throw new Error("No download link returned");
      }
    } catch (error) {
      alerts.error("Error downloading document");
    }
  };

  const hasIssues = documents.some((doc) =>
    issues.some(
      (issue) => issue.modelName === "Document" && issue.modelId === doc.id
    )
  );

  return (
    <TableContainer
      component={Paper}
      variant="outlined"
      sx={{
        border: `1px solid ${theme.palette.divider}`,
        "& .MuiTableCell-root": {
          borderColor: theme.palette.divider,
        },
      }}
    >
      <Table size="small">
        <TableHead>
          <TableRow
            sx={{
              backgroundColor: theme.palette.grey50.main,
            }}
          >
            <TableCell sx={{ fontWeight: 500 }}>Document name</TableCell>
            <TableCell sx={{ fontWeight: 500 }}>Type</TableCell>
            <TableCell sx={{ fontWeight: 500 }}>Status</TableCell>
            {hasIssues && (
              <TableCell sx={{ fontWeight: 500 }}>Issues</TableCell>
            )}
            <TableCell sx={{ width: 100 }}></TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {documents.map((doc) => {
            const issuesForDoc = issues.filter(
              (issue) =>
                issue.modelName === "Document" && issue.modelId === doc.id
            );
            return (
              <TableRow key={doc.id} hover>
                <TableCell sx={{ maxWidth: 200 }}>
                  <button
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation();
                      e.preventDefault();
                      handleDocumentDownload(doc);
                    }}
                    style={{
                      color: theme.palette.primary.main,
                      textDecoration: "underline",
                      cursor: "pointer",
                      border: "none",
                      background: "none",
                      padding: 0,
                      textAlign: "left",
                      fontSize: "inherit",
                      fontFamily: "inherit",
                      width: "100%",
                      overflow: "hidden",
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.color = theme.palette.primary.dark;
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.color = theme.palette.primary.main;
                    }}
                  >
                    <TruncatedText text={doc.name} maxLength={25} />
                  </button>
                </TableCell>
                <TableCell sx={{ maxWidth: 150 }}>
                  <TruncatedText text={doc.documentType} maxLength={20} />
                </TableCell>
                <DocumentStatusCell doc={doc} />
                {hasIssues && (
                  <TableCell>
                    <IssueCount issueCount={issuesForDoc.length} />
                  </TableCell>
                )}
                <TableCell sx={{ width: 100, whiteSpace: "nowrap" }}>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                    <IconButton
                      size="small"
                      onClick={(e) => {
                        e.stopPropagation();
                        onSelectDocument?.(doc);
                      }}
                      title="Preview document"
                      sx={{ color: theme.palette.primary.main }}
                    >
                      <Visibility fontSize="small" />
                    </IconButton>
                    <IconButton
                      size="small"
                      onClick={(e) => {
                        e.stopPropagation();
                        onDeleteDocument(doc.id);
                      }}
                      title="Delete document"
                    >
                      <Delete fontSize="small" />
                    </IconButton>
                  </Box>
                </TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
    </TableContainer>
  );
}
